import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, CircleNotch, XCircle } from "@phosphor-icons/react";
import { SEO } from "../components/site/SEO";
import { API, money, useCart } from "../cart";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("polling");
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setOrder(data); setState("paid"); clear(); return; }
      } catch (e) {}
      if (++attempts.current >= 8) { setState("pending"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <>
      <SEO title="Order Confirmation | Emporium London" description="Your Emporium London order confirmation." path="/payment/success" />
      <main className="relative z-10 min-h-[70vh] flex items-center justify-center px-6 pt-32 pb-20">
        <div className="max-w-xl text-center">
          {state === "polling" && <><CircleNotch size={44} className="animate-spin mx-auto text-[var(--gold)]" /><p className="mt-6 font-mono text-xs tracking-[0.2em] uppercase text-[var(--text-muted)]">Confirming your payment…</p></>}
          {state === "paid" && (
            <div data-testid="order-confirmed">
              <CheckCircle size={56} weight="thin" className="mx-auto text-[var(--brass)]" />
              <h1 className="mt-6 font-serif font-light text-4xl sm:text-5xl tracking-tight">Thank you — order confirmed</h1>
              <p className="mt-4 font-mono text-[11px] tracking-[0.2em] uppercase text-[var(--text-muted)]">Order {order.order_number}</p>
              <div className="mt-8 text-left bg-[var(--bg-secondary)] p-6">
                {order.items.map((i) => (<div key={i.product_id} className="flex justify-between text-sm py-1"><span className="text-[var(--text-secondary)]">{i.name} × {i.quantity}</span><span>{money(i.price * i.quantity)}</span></div>))}
                <div className="flex justify-between text-sm py-1 border-t border-black/10 mt-2 pt-2"><span>Shipping</span><span>{order.shipping === 0 ? "Free" : money(order.shipping)}</span></div>
                <div className="flex justify-between font-serif text-lg pt-1"><span>Total</span><span>{money(order.total)}</span></div>
                <p className="mt-2 font-mono text-[10px] text-[var(--text-muted)]">Includes VAT {money(order.vat_included)}. A confirmation email is on its way.</p>
              </div>
              <Link to="/shop" className="mt-8 inline-block border border-black/20 px-8 h-14 leading-[3.5rem] font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-[var(--text-primary)] hover:text-[var(--bg-primary)] transition-colors">Continue shopping</Link>
            </div>
          )}
          {state === "pending" && <><p className="font-serif text-2xl">Your payment is processing.</p><p className="mt-3 text-[var(--text-secondary)]">We'll email your confirmation shortly. You can safely close this page.</p></>}
          {state === "error" && <><XCircle size={48} className="mx-auto text-[var(--text-muted)]" /><p className="mt-4 font-serif text-2xl">We couldn't find that order.</p><Link to="/shop" className="mt-4 inline-block font-mono text-xs underline">Back to shop</Link></>}
        </div>
      </main>
    </>
  );
}

export function PaymentCancel() {
  return (
    <>
      <SEO title="Payment Cancelled | Emporium London" description="Your payment was cancelled." path="/payment/cancel" />
      <main className="relative z-10 min-h-[70vh] flex items-center justify-center px-6 pt-32 pb-20">
        <div className="max-w-lg text-center">
          <XCircle size={52} weight="thin" className="mx-auto text-[var(--text-muted)]" />
          <h1 className="mt-6 font-serif font-light text-4xl tracking-tight">Payment cancelled</h1>
          <p className="mt-4 text-[var(--text-secondary)]">No charge was made. Your basket has been kept so you can try again.</p>
          <div className="mt-8 flex gap-3 justify-center">
            <Link to="/cart" className="border border-black/20 px-6 h-12 leading-[3rem] font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-[var(--text-primary)] hover:text-[var(--bg-primary)] transition-colors">Return to basket</Link>
            <Link to="/shop" className="bg-[var(--text-primary)] text-[var(--bg-primary)] px-6 h-12 leading-[3rem] font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-[var(--brass)] transition-colors">Continue shopping</Link>
          </div>
        </div>
      </main>
    </>
  );
}
